import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import {
  Button, Col, Table, Row, Typography, Icon, Input, Tag, Tooltip,
} from 'antd';
import { DATE_FORMAT } from '../bynx/packageUtil';
import './PurchaseCredits.css';
import { asViaUrlWithSession } from '../../../utility/via-fetch';

const { Column } = Table;
const { Text } = Typography;


// TABLE
export default function PurchasesTable({
  isLoading, purchases, includeCompanyName, includePackageDetails, editColumn, viewInvoice,
}) {
  const [visiblePurchases, setVisiblePurchases] = useState([]);

  useEffect(() => {
    const tempPurchases = purchases.slice();
    setVisiblePurchases(tempPurchases.map((purchase) => ({
      ...purchase,
      purchaseDateTimeDisplayable: moment.utc(purchase.purchaseDateTime).format(DATE_FORMAT),
      expiryDate: purchase.expiryDate ? moment.utc(purchase.expiryDate).format(DATE_FORMAT) : 'N/A',
      price: purchase.price.toFixed(2),
      vatCharge: typeof (purchase.vatCharge) === 'number' ? purchase.vatCharge.toFixed(2) : 'N/A',
    })));
  }, [purchases]);

  const intl = useIntl();
  const RowTotal = () => (
    <div>
      <Text>{`${visiblePurchases.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  function getColumnSearchProps(dataIndex, searchBoxText) {
    let searchInput = () => {};
    const searchBoxBackGroundText = `${intl.formatMessage({ id: 'search' })} ${searchBoxText}`;

    return {
      filterDropdown: ({
        // eslint-disable-next-line react/prop-types
        setSelectedKeys, selectedKeys, confirm, clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={(node) => { searchInput = node; }}
            placeholder={searchBoxBackGroundText}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            icon="search"
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            {intl.formatMessage({ id: 'search' })}
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            {intl.formatMessage({ id: 'reset' })}
          </Button>
        </div>
      ),
      filterIcon: (filtered) => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => (record[dataIndex].toLowerCase().includes(value.toLowerCase())),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.select());
        }
      },
    };
  }

  // SORTS
  // This is using the actialy date time rather than the display date so it can sort by time aswell as date
  const sortByDate = (a, b) => {
    const adate = moment.utc(a.purchaseDateTime);
    const bdate = moment.utc(b.purchaseDateTime);
    if (adate.isBefore(bdate)) return 1;
    if (adate.isAfter(bdate)) return -1;
    return 0;
  };

  function sortByCompanyName(a, b) {
    const aStr = `${a.companyName}`;
    const bStr = `${b.companyName}`;
    return aStr.localeCompare(bStr);
  }

  // TABLE SETTINGS AND COLUMNS
  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={visiblePurchases}
      rowKey={(purchase) => purchase.code}
      expandIcon={({ expanded, record, onExpand }) => (
        includePackageDetails && (
          <Row type="flex" align="middle" className="expandable-icon">
            <Button type="link" size="small" onClick={e => onExpand(record, e)}>
              <FormattedMessage id={expanded ? 'hide' : 'view'} />
            </Button>
          </Row>
        ))}
      expandIconAsCell={false}
      expandIconColumnIndex={includeCompanyName ? 2 : 1}
      expandedRowRender={(record) => (
        <Row type="flex" align="middle" justify="space-around">
          <Col span={6}>
            <Row type="flex" align="middle" justify="space-around" style={{ marginBottom: '1em' }}>
              <Col span={10}>
                <b><FormattedMessage id="purchasedCredits" /></b>
                :&nbsp;
              </Col>
              <Col span={10}>
                {record.mainTokens}
              </Col>
            </Row>
            <Row type="flex" align="middle" justify="space-around">
              <Col span={10}>
                <b><FormattedMessage id="bonusCredits" /></b>
                :&nbsp;
              </Col>
              <Col span={10}>{record.extraTokens}</Col>
            </Row>
          </Col>
          <Col span={6}>
            <Row type="flex" align="middle" justify="space-around" style={{ marginBottom: '1em' }}>
              <Col span={8}>
                <b><FormattedMessage id="price" /></b>
                :&nbsp;
              </Col>
              <Col span={10}>{`${record.price} ${record.currencyCode}`}</Col>
            </Row>
            <Row type="flex" align="middle" justify="space-around">
              <Col span={8}>
                <b><FormattedMessage id="vat" /></b>
                :&nbsp;
              </Col>
              <Col span={10}>{`${record.vatCharge} ${record.vatCharge !== 'N/A' ? record.currencyCode : ''}`}</Col>
            </Row>
          </Col>
        </Row>
      )}
    >
      {includeCompanyName && (
        <Column
          title={<FormattedMessage id="customerName" />}
          dataIndex="companyName"
          key="companyName"
          sorter={(a, b) => sortByCompanyName(a, b)}
          {...getColumnSearchProps('companyName', intl.formatMessage({ id: 'customerName' }))}
        />
      )}
      <Column
        title={<FormattedMessage id="purchaseDate" />}
        dataIndex="purchaseDateTimeDisplayable"
        key="purchaseDateTimeDisplayable"
        sorter={(a, b) => sortByDate(a, b)}
        defaultSortOrder="ascend"
      />
      {includePackageDetails && (
        <Column
          title={<FormattedMessage id="packageName" />}
          dataIndex="packageTitle"
          key="packageTitle"
          className="expandable-col"
          render={(text) => (<div className="expandable-col-content">{text || 0}</div>)}
        />
      )}
      <Column title={<FormattedMessage id="purchaseID" />} dataIndex="code" key="code" />
      <Column
        title={<FormattedMessage id="purchaseStatus" />}
        dataIndex="purchaseStatus"
        key="purchaseStatus"
        align="center"
        render={(text) => {
          if (text === 'FAILED') return <Tag color="red">{text}</Tag>;
          if (text === 'CONFIRMING') return <Tag color="purple">{text}</Tag>;
          if (text === 'CONFIRMED') return <Tag color="green">{text}</Tag>;
          return text;
        }}
      />
      <Column title={<FormattedMessage id="creditBalance" />} dataIndex="tokensRemaining" key="tokensRemaining" align="right" />
      <Column title={<FormattedMessage id="expiryDate" />} dataIndex="expiryDate" key="expiryDate" />
      { editColumn }
      { viewInvoice
        ? (
          <Column
            className="via-action-column"
            align="center"
            width="50px"
            render={(text, record) => (
              record.purchaseStatus === 'CONFIRMED' && !record.registration && !record.adHoc
                ? (
                  <a href={asViaUrlWithSession(`/api/tokens/purchase/${viewInvoice}/${record.code}`)} rel="noreferrer noopener" target="_blank">
                    <Tooltip title={intl.formatMessage({ id: 'viewInvoice' })}>
                      <Button type="primary" icon="audit" style={{ fontSize: '16pt', width: '30px', paddingLeft: '0.15em' }} />
                    </Tooltip>
                  </a>
                )
                : ''
            )}
          />
        )
        : null }
    </Table>
  );
}

PurchasesTable.propTypes = {
  isLoading: PropTypes.bool,
  viewInvoice: PropTypes.string,
  editColumn: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  purchases: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      purchaseDateTime: PropTypes.string.isRequired,
      packageTitle: PropTypes.string.isRequired,
      purchaseStatus: PropTypes.string.isRequired,
      currencyCode: PropTypes.string.isRequired,
      mainTokens: PropTypes.number.isRequired,
      extraTokens: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
      tokensRemaining: PropTypes.number.isRequired,
      vatCharge: PropTypes.number,
      expiryDate: PropTypes.string,
    }),
  ),
  includeCompanyName: PropTypes.bool,
  includePackageDetails: PropTypes.bool,
};

PurchasesTable.defaultProps = {
  isLoading: false,
  viewInvoice: null,
  editColumn: null,
  purchases: [],
  includeCompanyName: false,
  includePackageDetails: true,
};
